import { useContext, useState } from "react"
import firebase from '../../../firebase/compat'
import { v4 as uuid } from 'uuid'
import { db } from '../../../firebase/config'
import { useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore'
import { client } from '../../../hooks/Client'
import Location from '../../../hooks/Location'
import { Auth } from '../../../StateManagment/Auth'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import { FAILED_OUTCOMES } from '../../../helpers/contactMoments/computeFailedStreak'
import {
    toDateTimeInputValue,
    parseDateTimeInputValue,
    isAllowedContactMomentTime,
    toContactMomentDate,
    formatContactMomentWhen,
} from '../../../helpers/contactMoments/contactMomentTime'
import Table from '../../common/table/Table.jsx'
import TableCell from '../../common/table/TableCell.jsx'
import Modal from '../../common/Modal'
import DeleteModal from '../../common/DeleteModal'
import Tooltip from '../../common/Tooltip'
import NoteBodyCell from '../../notes/NoteBodyCell'
import UserImageMeta from '../../user/UserImageMeta'
import UserNameMeta from '../../user/UserNameMeta'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { TYPE_OPTIONS, OUTCOME_OPTIONS, TYPE_LABELS, OUTCOME_LABELS } from './contactMomentLabels'
import MarkUnreachableButton from './MarkUnreachableButton'
import ReadOnlyGuard from '../../common/ReadOnlyGuard'
import softDeleteDoc from '../../../helpers/softDeleteDoc';

const ContactMoments = ({ adressNavigation, adressDocid, adressStatus, unreachableAfterDays = null }) => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    const adressId = Location()[3]

    const contactMoments = useFirestoreGeneralOrderBy(
        'ContactMoments',
        'AdressID',
        adressId || '',
        'Timestamp',
        'desc'
    )

    const [addModalOpen, setAddModalOpen] = useState(false)
    const [formType, setFormType] = useState('call')
    const [formOutcome, setFormOutcome] = useState('reached')
    const [formBody, setFormBody] = useState('')
    // Waarde van het datetime-local-veld; wordt op 'nu' gezet bij het openen.
    const [formWhen, setFormWhen] = useState('')
    const formWhenDate = parseDateTimeInputValue(formWhen)
    const formWhenAllowed = isAllowedContactMomentTime(formWhenDate, new Date())

    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('dit contactmoment')

    const resetForm = () => {
        setFormType('call')
        setFormOutcome('reached')
        setFormBody('')
        setFormWhen('')
    }

    const openAddModal = () => {
        setFormWhen(toDateTimeInputValue(new Date()))
        setAddModalOpen(true)
    }

    const addContactMoment = async () => {
        if (!formWhenAllowed) return
        const newId = uuid()
        await db.collection('ContactMoments').add({
            ID: newId,
            AdressID: adressId,
            CompagnyID: client,
            Compagny: client,
            Type: formType,
            Outcome: formOutcome,
            Body: formBody,
            Position: (contactMoments?.length || 0) + 1,
            User: auth.ID,
            // Het moment van contact, niet van invoeren: de lijst en de reeks
            // mislukte pogingen sorteren hierop.
            Timestamp: firebase.firestore.Timestamp.fromDate(toContactMomentDate(formWhenDate, new Date())),
        })
        resetForm()
        setAddModalOpen(false)
        setSaved('flex')
    }

    const deleteModalHandler = (e) => {
        setDeleteDocid(e.currentTarget.dataset.docid)
        setDeleteName('dit contactmoment')
        setDeleteModal(true)
    }

    const deleteContactMoment = () => {
        softDeleteDoc({ collection: 'ContactMoments', docid: deleteDocid }).then(() => {
            setSaved('flex')
        })
    }

    const outcomeChipClass = (outcome) => {
        if (outcome === 'reached') return 'contact-moment-chip contact-moment-chip-reached'
        if (FAILED_OUTCOMES.includes(outcome)) return 'contact-moment-chip contact-moment-chip-failed'
        if (outcome === 'moved') return 'contact-moment-chip contact-moment-chip-moved'
        return 'contact-moment-chip contact-moment-chip-other'
    }

    return (
        <div style={{ display: adressNavigation === 'contactMoments' ? 'block' : 'none' }}>
            <div className="persona-option-container">
                <div className="table-container">
                    <MarkUnreachableButton
                        adressID={adressId}
                        contactMoments={contactMoments}
                        adressDocid={adressDocid}
                        currentStatus={adressStatus}
                        afterDays={unreachableAfterDays}
                    />
                    <ReadOnlyGuard edit="addresses.edit">
                        {(canEdit) => (
                            <Table
                                columns={[
                                    { key: 'date', label: 'DATUM EN TIJD', searchable: false },
                                    { key: 'type', label: 'TYPE' },
                                    { key: 'outcome', label: 'RESULTAAT' },
                                    { key: 'author', label: 'AUTEUR', searchable: false },
                                    { key: 'body', label: 'NOTITIE', searchable: false },
                                    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                                ]}
                                items={contactMoments || []}
                                getItemKey={(cm) => cm.ID}
                                renderRow={(cm) => (
                                    <>
                                        <TableCell>{formatContactMomentWhen(cm)}</TableCell>
                                        <TableCell>{TYPE_LABELS[cm.Type] || cm.Type}</TableCell>
                                        <TableCell>
                                            <span className={outcomeChipClass(cm.Outcome)}>
                                                {OUTCOME_LABELS[cm.Outcome] || cm.Outcome}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div id="report-user-meta-container">
                                                <UserImageMeta userID={cm.User} />
                                                <UserNameMeta userID={cm.User} />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <NoteBodyCell body={cm.Body} />
                                        </TableCell>
                                        <TableCell>
                                            {canEdit && (
                                                <Tooltip content={'Contactmoment verwijderen'} top="-60px" width={'30px'}>
                                                    <DeleteOutlinedIcon
                                                        className="table-delete-icon"
                                                        data-docid={cm.docid}
                                                        onClick={deleteModalHandler}
                                                    />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </>
                                )}
                                searchAccessor={(cm) => `${TYPE_LABELS[cm?.Type] || ''} ${OUTCOME_LABELS[cm?.Outcome] || ''}`}
                                searchPlaceholder="Zoeken op type of resultaat"
                                emptyMessage="Geen contactmomenten geregistreerd."
                                addRow={canEdit ? {
                                    toolTipContent: 'Contactmoment toevoegen',
                                    onClick: openAddModal,
                                } : undefined}
                            />
                        )}
                    </ReadOnlyGuard>

                    <Modal
                        modalOpen={addModalOpen}
                        setModalOpen={setAddModalOpen}
                        pageHeader={'Contactmoment toevoegen'}
                        confirm={addContactMoment}
                        confirmLabel={'Opslaan'}
                        confirmDisabled={!formWhenAllowed}
                        content={
                            <ReadOnlyGuard edit="addresses.edit">
                            <div className="contact-moment-form">
                                <label className="contact-moment-field">
                                    <span>Datum en tijd</span>
                                    <input
                                        type="datetime-local"
                                        value={formWhen}
                                        max={toDateTimeInputValue(new Date())}
                                        onChange={(e) => setFormWhen(e.target.value)}
                                    />
                                    {!formWhenAllowed && (
                                        <span className="contact-moment-field-error">
                                            Kies een datum en tijd die niet in de toekomst ligt.
                                        </span>
                                    )}
                                </label>
                                <label className="contact-moment-field">
                                    <span>Type</span>
                                    <select value={formType} onChange={(e) => setFormType(e.target.value)}>
                                        {TYPE_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="contact-moment-field">
                                    <span>Resultaat</span>
                                    <select value={formOutcome} onChange={(e) => setFormOutcome(e.target.value)}>
                                        {OUTCOME_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="contact-moment-field">
                                    <span>Notitie (optioneel)</span>
                                    <textarea
                                        rows={4}
                                        value={formBody}
                                        onChange={(e) => setFormBody(e.target.value)}
                                    />
                                </label>
                            </div>
                            </ReadOnlyGuard>
                        }
                    />

                    <DeleteModal
                        deleteModal={deleteModal}
                        setDeleteModal={setDeleteModal}
                        deleteItem={deleteContactMoment}
                        deleteName={deleteName}
                        softDelete
                      />
                </div>
            </div>
        </div>
    )
}

export default ContactMoments
